/**
 * Video Thumbnail Generation Utilities
 * Uses HTML5 video element and canvas to extract frames
 */

/**
 * Generate thumbnail from video file at 0.5 seconds
 * Uses HTML5 video element and canvas to extract frame
 *
 * @param videoFile - Video file to generate thumbnail from
 * @returns Promise with thumbnail blob and data URL
 */
export const generateThumbnail = async (
  videoFile: File
): Promise<{ blob: Blob; dataUrl: string }> => {
  console.log('[VideoThumbnail] Generating thumbnail...', {
    fileName: videoFile.name,
    fileSize: videoFile.size,
    fileType: videoFile.type,
  });

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      console.error('[VideoThumbnail] Canvas context not available');
      reject(new Error('Canvas context not available'));
      return;
    }

    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    // 30-second timeout for metadata loading
    const metadataTimeoutId = setTimeout(() => {
      console.error('[VideoThumbnail] Metadata loading timeout', {
        fileName: videoFile.name,
      });
      URL.revokeObjectURL(video.src);
      reject(
        new Error(
          'Video metadata loading timed out. The video file may be corrupted.'
        )
      );
    }, 30000);

    // Timeout for seek operation
    let seekTimeoutId: number | null = null;

    video.onloadedmetadata = () => {
      // Clear metadata timeout
      clearTimeout(metadataTimeoutId);

      // Set canvas size to video dimensions
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      console.log('[VideoThumbnail] Video metadata loaded', {
        width: video.videoWidth,
        height: video.videoHeight,
        duration: video.duration,
      });

      // Start seek timeout
      seekTimeoutId = window.setTimeout(() => {
        console.error('[VideoThumbnail] Seek operation timeout', {
          fileName: videoFile.name,
        });
        URL.revokeObjectURL(video.src);
        reject(
          new Error(
            'Video seek operation timed out. The video file may be corrupted.'
          )
        );
      }, 10000);

      // Seek to 0.5 seconds (match mobile implementation)
      video.currentTime = Math.min(0.5, video.duration);
    };

    video.onseeked = () => {
      // Clear seek timeout
      if (seekTimeoutId !== null) {
        clearTimeout(seekTimeoutId);
      }

      try {
        // Draw video frame to canvas
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert to blob with 0.8 quality (match mobile)
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              console.error('[VideoThumbnail] Canvas.toBlob returned null', {
                fileName: videoFile.name,
                canvasWidth: canvas.width,
                canvasHeight: canvas.height,
              });
              URL.revokeObjectURL(video.src);
              reject(new Error('Failed to create thumbnail blob'));
              return;
            }

            const dataUrl = canvas.toDataURL('image/jpeg', 0.8);

            console.log('[VideoThumbnail] Thumbnail generated', {
              blobSize: blob.size,
            });

            // Cleanup
            URL.revokeObjectURL(video.src);

            resolve({ blob, dataUrl });
          },
          'image/jpeg',
          0.8
        );
      } catch (error) {
        console.error('[VideoThumbnail] Canvas rendering failed', {
          error,
          fileName: videoFile.name,
          videoWidth: video.videoWidth,
          videoHeight: video.videoHeight,
          canvasWidth: canvas.width,
          canvasHeight: canvas.height,
        });
        URL.revokeObjectURL(video.src);
        reject(error);
      }
    };

    video.onerror = (event) => {
      // Clear both timeouts
      clearTimeout(metadataTimeoutId);
      if (seekTimeoutId !== null) {
        clearTimeout(seekTimeoutId);
      }

      console.error('[VideoThumbnail] Failed to load video', {
        error: event,
        fileName: videoFile.name,
        fileSize: videoFile.size,
        fileType: videoFile.type,
      });
      URL.revokeObjectURL(video.src);
      reject(
        new Error(
          'Failed to load video for thumbnail generation. The video file may be corrupted or in an unsupported format.'
        )
      );
    };

    video.src = URL.createObjectURL(videoFile);
  });
};

/**
 * Get video duration in seconds
 *
 * @param videoFile - Video file to extract duration from
 * @returns Promise with duration in seconds (rounded)
 */
export const getVideoDuration = async (videoFile: File): Promise<number> => {
  console.log('[VideoThumbnail] Extracting video duration...', {
    fileName: videoFile.name,
    fileSize: videoFile.size,
    fileType: videoFile.type,
  });

  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    // 30-second timeout
    const timeoutId = setTimeout(() => {
      console.error('[VideoThumbnail] Duration extraction timeout', {
        fileName: videoFile.name,
      });
      URL.revokeObjectURL(video.src);
      reject(
        new Error(
          'Video duration extraction timed out. The video file may be corrupted.'
        )
      );
    }, 30000);

    video.onloadedmetadata = () => {
      // Clear timeout
      clearTimeout(timeoutId);

      const duration = Math.round(video.duration);
      console.log('[VideoThumbnail] Duration extracted', {
        fileName: videoFile.name,
        duration,
      });
      URL.revokeObjectURL(video.src);
      resolve(duration);
    };

    video.onerror = (event) => {
      // Clear timeout
      clearTimeout(timeoutId);

      console.error('[VideoThumbnail] Failed to extract video duration', {
        error: event,
        fileName: videoFile.name,
        fileSize: videoFile.size,
        fileType: videoFile.type,
      });
      URL.revokeObjectURL(video.src);
      reject(
        new Error(
          'Failed to extract video duration. The video file may be corrupted or in an unsupported format.'
        )
      );
    };

    video.src = URL.createObjectURL(videoFile);
  });
};
